import React, { type MouseEvent, useEffect, useRef } from 'react';

import useAnimationFrame from '@phntms/use-animation-frame';
import { AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { useTheme } from 'styled-components';

import { BORDER_STROKE_RADIUS, BORDER_STROKE_WIDTH, OutlineInner, OutlineMask } from './styles';

interface Vector2 {
    x: number;
    y: number;
}

interface Props {
    className?: string;
    show?: boolean;
    interactWithCursorOnHover?: boolean;
    primaryColor: string;
    secondaryColor: string;
    baseColor?: string;
    outlineOffset?: number;
    dashed?: boolean;
    borderStrokeWidth?: number;
    borderStrokeRadius?: number;
    transformScale?: boolean;
    useMotionValues?: boolean;
}

const YatAnimatedOutline = ({
    className,
    show,
    interactWithCursorOnHover,
    primaryColor,
    secondaryColor,
    baseColor,
    outlineOffset = 0,
    dashed,
    useMotionValues,
    borderStrokeWidth = BORDER_STROKE_WIDTH,
    borderStrokeRadius = BORDER_STROKE_RADIUS,
    transformScale = true,
}: Props) => {
    const theme = useTheme();
    const maskRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    const hoveringRef = useRef<boolean>(false);
    const cursorRef = useRef<Vector2>({ x: 0, y: 0 });

    // Set random rotation on load (between 0 and 360)
    // const rotationRef = useRef(Math.random() * 360);
    const rotationRef = useRef(0);

    useEffect(() => {
        rotationRef.current = 0;
    }, [show]);

    /**
     * This function takes a new rotation, and fixes flipping that happens when trying to animate between degree
     * boundaries.
     *
     * **The issue**:
     * When stepping forward from 340 -> 350 degrees, the smoothed value animates clockwise. Whereas stepping from
     * 359 -> 0, the value animates the opposite direction counter-clockwise. The reverse happens when stepping
     * backward from 0 -> 359 degrees.
     *
     * This function fixes this by taking the difference in value from previous rotation to new rotation, and then
     * calculating new value based on the difference. Doing this also means the value is no longer fixed to 360 degrees
     * and rather goes beyond this to fix the smoothed flipping behavior.
     */
    const setRotation = (rotation: number) => {
        const angle = rotationRef.current % 360;
        let difference = (360 - (angle - rotation)) % 360;

        if (difference > 180) {
            difference = 360 - difference;
            rotationRef.current = rotationRef.current - difference;
        } else rotationRef.current = rotationRef.current + difference;
    };

    const animate = () => {
        if (!maskRef.current || !innerRef.current) return;

        // If not hovering, increment rotation
        if (!interactWithCursorOnHover || !hoveringRef.current) rotationRef.current += 2;
        // Else, match rotation of background based on cursor position
        else {
            const elementX = maskRef.current.getBoundingClientRect().left;
            const elementY = maskRef.current.getBoundingClientRect().top;

            const elementWidth = maskRef.current.clientWidth / 2;
            const elementHeight = maskRef.current.clientHeight / 2;

            const elementCenterX = elementX + elementWidth;
            const elementCenterY = elementY + elementHeight;

            const mouseX = cursorRef.current.x;
            const mouseY = cursorRef.current.y;
            const radians = Math.atan2(mouseX - elementCenterX, mouseY - elementCenterY);
            const degree = radians * (180 / Math.PI);
            const to360 = Math.abs(degree + 180 - 360);

            setRotation(to360);
        }

        gsap.set(innerRef.current, { rotate: rotationRef.current });
    };

    useAnimationFrame(animate, 12);

    const handleMouseMove = (event: MouseEvent<HTMLElement>) => {
        hoveringRef.current = true;
        cursorRef.current.x = event.clientX;
        cursorRef.current.y = event.clientY;
    };

    const handleMouseLeave = () => (hoveringRef.current = false);

    const motionValues = useMotionValues
        ? {
              initial: { opacity: 0, scale: transformScale ? 0 : 1 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: transformScale ? 0 : 1 },
              transition: { duration: theme.transitions.speeds.normal, ease: theme.transitions.eases.cubic },
          }
        : {};

    return (
        <AnimatePresence>
            {show && (
                <OutlineMask
                    className={className}
                    ref={maskRef}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    {...motionValues}
                    $outlineOffset={outlineOffset}
                    $dashed={dashed}
                    $borderStrokeWidth={borderStrokeWidth}
                    $borderStrokeRadius={borderStrokeRadius}
                >
                    <OutlineInner
                        ref={innerRef}
                        $primaryColor={primaryColor}
                        $secondaryColor={secondaryColor}
                        $baseColor={baseColor || theme.shades.white[20]}
                    />
                </OutlineMask>
            )}
        </AnimatePresence>
    );
};

export default YatAnimatedOutline;
